import React, { useState, useEffect, useMemo } from 'react'
import { Box, Typography, IconButton, Button, Divider } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import supabase from '../../../../supabaseClient';
import { applyRoundingRules, findMatchingRule } from '../../../../Utils/roundingRules';

const HOURS_COLOR = '#A84355';
const PAYMENTS_COLOR = '#059669';
const REMAINING_COLOR = '#B5541F';

const MONTH_NAMES = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const ROLE_FILTERS = [
    { value: 'all', label: 'Wszyscy' },
    { value: 'drivers', label: 'Kierowcy' },
    { value: 'production', label: 'Produkcja' },
];

const STATUS_FILTERS = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'due', label: 'Do rozliczenia' },
    { value: 'settled', label: 'Rozliczone' },
];

const pad = (n) => String(n).padStart(2, '0');
const monthKeyOf = (d) => `${d.getFullYear()}-${d.getMonth()}`;

const formatPln = (v) =>
    `${v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

const formatHoursMinutes = (totalMinutes) => {
    const h = Math.floor(totalMinutes / 60);
    const m = Math.round(totalMinutes % 60);
    return `${h}h ${pad(m)}m`;
};

const billableMinutesOf = (record, rules) => {
    const matched = findMatchingRule(record.check_in, rules);
    if (matched) return applyRoundingRules(record.check_in, record.check_out, matched).billableMinutes;
    return Math.max(0, (new Date(record.check_out) - new Date(record.check_in)) / 60000);
};

function StatCard({ label, value, color, hint }) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 200,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'secondary.light',
                borderTop: '3px solid',
                borderTopColor: color,
                borderRadius: '12px',
                px: 2.5,
                py: 2,
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                }}
            >
                {label}
            </Typography>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color, lineHeight: 1.3, mt: 0.5 }}>
                {value}
            </Typography>
            {hint && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                    {hint}
                </Typography>
            )}
        </Box>
    );
}

function FilterPill({ active, onClick, children }) {
    return (
        <Button
            onClick={onClick}
            variant={active ? 'contained' : 'outlined'}
            disableElevation
            sx={{
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.3px',
                px: 2,
                py: 0.75,
                color: active ? 'primary.contrastText' : 'primary.main',
                backgroundColor: active ? 'primary.main' : 'background.paper',
                borderColor: active ? 'primary.main' : 'secondary.light',
                '&:hover': {
                    backgroundColor: active ? 'primary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                },
            }}
        >
            {children}
        </Button>
    );
}

function HoursReportFilter({ idConfig, onFilterChange, refreshTick }) {
    const [viewDate, setViewDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const [employees, setEmployees] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [payments, setPayments] = useState([]);
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!idConfig) return;
        const fetchData = async () => {
            setLoading(true);

            const [
                { data: employeesData, error: employeesError },
                { data: attendanceData, error: attendanceError },
                { data: paymentsData, error: paymentsError },
                { data: rulesData, error: rulesError },
            ] = await Promise.all([
                supabase
                    .from('employees')
                    .select('id, name, surname, hourly_rate, isDriver, archived')
                    .eq('id_configuration', idConfig)
                    .is('archived', null),
                supabase
                    .from('attendance')
                    .select('employee_id, check_in, check_out')
                    .eq('id_configuration', idConfig)
                    .not('check_in', 'is', null)
                    .not('check_out', 'is', null),
                supabase
                    .from('payments')
                    .select('employee_id, amount, date, period_month')
                    .eq('id_configuration', idConfig),
                supabase
                    .from('rounding_rules')
                    .select('*')
                    .eq('id_configuration', idConfig)
                    .eq('is_active', true),
            ]);

            if (!employeesError) setEmployees(employeesData || []);
            if (!attendanceError) setAttendance(attendanceData || []);
            if (!paymentsError) setPayments(paymentsData || []);
            if (!rulesError) setRules(rulesData || []);

            setLoading(false);
        };

        fetchData();
    }, [idConfig, refreshTick]);

    const employeeById = useMemo(() => {
        const map = {};
        employees.forEach((e) => { map[e.id] = e; });
        return map;
    }, [employees]);

    const roleFilteredEmployees = useMemo(() => employees.filter((e) => {
        if (roleFilter === 'drivers') return !!e.isDriver;
        if (roleFilter === 'production') return !e.isDriver;
        return true;
    }), [employees, roleFilter]);

    const roleFilteredIds = useMemo(
        () => new Set(roleFilteredEmployees.map((e) => e.id)),
        [roleFilteredEmployees]
    );

    // Per employee, per calendar month: how much was earned vs. paid.
    const monthlyBalances = useMemo(() => {
        const buckets = {};
        const ensure = (empId, key) => {
            const bucketKey = `${empId}|${key}`;
            if (!buckets[bucketKey]) buckets[bucketKey] = { employeeId: empId, monthKey: key, earned: 0, paid: 0 };
            return buckets[bucketKey];
        };

        attendance.forEach((r) => {
            if (!roleFilteredIds.has(r.employee_id)) return;
            const emp = employeeById[r.employee_id];
            if (!emp) return;
            const minutes = billableMinutesOf(r, rules);
            const key = monthKeyOf(new Date(r.check_in));
            ensure(r.employee_id, key).earned += (minutes / 60) * (emp.hourly_rate || 0);
        });

        payments.forEach((p) => {
            if (!roleFilteredIds.has(p.employee_id)) return;
            // Bucket by the period the payment settles, not the day it was made.
            const key = monthKeyOf(new Date(p.period_month || p.date));
            ensure(p.employee_id, key).paid += p.amount || 0;
        });

        return Object.values(buckets);
    }, [attendance, payments, rules, roleFilteredIds, employeeById]);

    const selectedKey = monthKeyOf(viewDate);

    const stats = useMemo(() => {
        let hoursMinutes = 0;
        attendance.forEach((r) => {
            if (!roleFilteredIds.has(r.employee_id)) return;
            if (monthKeyOf(new Date(r.check_in)) !== selectedKey) return;
            hoursMinutes += billableMinutesOf(r, rules);
        });

        let paidInMonth = 0;
        payments.forEach((p) => {
            if (!roleFilteredIds.has(p.employee_id)) return;
            if (monthKeyOf(new Date(p.period_month || p.date)) !== selectedKey) return;
            paidInMonth += p.amount || 0;
        });

        let totalRemaining = 0;
        let currentMonthRemaining = 0;
        monthlyBalances.forEach((bucket) => {
            const remaining = Math.max(0, bucket.earned - bucket.paid);
            totalRemaining += remaining;
            if (bucket.monthKey === selectedKey) currentMonthRemaining += remaining;
        });
        const previousRemaining = Math.max(0, totalRemaining - currentMonthRemaining);

        return { hoursMinutes, paidInMonth, totalRemaining, currentMonthRemaining, previousRemaining };
    }, [attendance, payments, monthlyBalances, roleFilteredIds, rules, selectedKey]);

    const employeeSummaries = useMemo(() => roleFilteredEmployees.map((emp) => {
        const remaining = monthlyBalances
            .filter((b) => b.employeeId === emp.id)
            .reduce((sum, b) => sum + Math.max(0, b.earned - b.paid), 0);
        return { ...emp, remaining, settled: remaining <= 0.005 };
    }), [roleFilteredEmployees, monthlyBalances]);

    const statusFilteredEmployees = useMemo(() => employeeSummaries.filter((e) => {
        if (statusFilter === 'due') return !e.settled;
        if (statusFilter === 'settled') return e.settled;
        return true;
    }), [employeeSummaries, statusFilter]);

    useEffect(() => {
        if (!onFilterChange) return;
        const rangeFrom = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
        const rangeTo = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
        onFilterChange({
            dateFrom: `${rangeFrom.getFullYear()}-${pad(rangeFrom.getMonth() + 1)}-${pad(rangeFrom.getDate())}`,
            dateTo: `${rangeTo.getFullYear()}-${pad(rangeTo.getMonth() + 1)}-${pad(rangeTo.getDate())}`,
            roleFilter,
            statusFilter,
            loading,
            employees: statusFilteredEmployees,
            stats,
            rules,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewDate, roleFilter, statusFilter, loading, statusFilteredEmployees, stats]);

    const rangeFrom = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const rangeTo = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    const monthLabel = `${MONTH_NAMES[viewDate.getMonth()]} ${viewDate.getFullYear()}`;
    const rangeLabel = `${pad(rangeFrom.getDate())}.${pad(rangeFrom.getMonth() + 1)} – ${pad(rangeTo.getDate())}.${pad(rangeTo.getMonth() + 1)}.${rangeTo.getFullYear()}`;

    const goPrev = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const goNext = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

    return (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton
                    size="small"
                    onClick={goPrev}
                    sx={{ border: '1px solid', borderColor: 'secondary.light', borderRadius: '10px' }}
                >
                    <ChevronLeftIcon />
                </IconButton>

                <Box
                    sx={{
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: 'secondary.light',
                        borderRadius: '10px',
                        px: 3,
                        py: 1,
                        minWidth: 220,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Roboto Condensed", sans-serif',
                            fontWeight: 700,
                            color: 'primary.main',
                            fontSize: '1.05rem',
                        }}
                    >
                        {monthLabel}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {rangeLabel}
                    </Typography>
                </Box>

                <IconButton
                    size="small"
                    onClick={goNext}
                    sx={{ border: '1px solid', borderColor: 'secondary.light', borderRadius: '10px' }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <StatCard label="Godziny pracy" value={formatHoursMinutes(stats.hoursMinutes)} color={HOURS_COLOR} />
                <StatCard label="Wypłacono" value={formatPln(stats.paidInMonth)} color={PAYMENTS_COLOR} />
                <StatCard
                    label="Pozostało do wypłaty"
                    value={formatPln(stats.currentMonthRemaining + stats.previousRemaining)}
                    color={REMAINING_COLOR}
                    hint={
                        stats.previousRemaining > 0.005
                            ? `w tym ${formatPln(stats.previousRemaining)} z wcześniejszych okresów`
                            : null
                    }
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {ROLE_FILTERS.map((f) => (
                    <FilterPill key={f.value} active={roleFilter === f.value} onClick={() => setRoleFilter(f.value)}>
                        {f.label}
                    </FilterPill>
                ))}

                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'secondary.light' }} />

                {STATUS_FILTERS.map((f) => (
                    <FilterPill key={f.value} active={statusFilter === f.value} onClick={() => setStatusFilter(f.value)}>
                        {f.label}
                    </FilterPill>
                ))}
            </Box>
        </Box>
    );
}

export default HoursReportFilter;
